/* eslint-disable no-undef-init */
import React, { useContext, useEffect, useMemo } from 'react';

import 'mapbox-gl/dist/mapbox-gl.css';

import { handleEventGoogle } from 'models/shared/helpers/googleAnalytics';

import { ReactComponent as Check } from 'design_system/src/static/icons/checkmark.svg';

import '../recovery.scss';
import { Button, Typography } from 'design_system/src';
import {
	validationItem,
	validationItemState,
} from 'components/hocs/messages/messages';
import {
	CreateRouteSteps,
	CreateRouteStepsValues,
	RepossessionTabs,
	RepossessionTabsValues,
} from '../Repossessions.constants';
import { repossessionContext } from '../RepossessionsContext';
import { AllRepossessionsMap } from './components/1.AllRepossessionsMap';
import { FilterRepossessionsTable } from './components/2.FilterRepossessionsTable';
import { SelectInterestPointsMap } from './components/3.SelectInterestPointsMap';
import { CreateRouteMap } from './components/4.CreateRouteMap';

export const CreateRoute = () => {
	const {
		viewTab,
		setViewTab,
		createRouteStep,
		setCreateRouteStep,
		selectedMarkers,
	} = useContext(repossessionContext);

	useEffect(() => {
		handleEventGoogle(
			'VIEW_Panda_repossessions',
			'Panda',
			'page view to the repossessions map view',
		);
	}, []);

	const stepValidations = useMemo(() => {
		switch (createRouteStep) {
			case CreateRouteSteps.allRepossessions:
				return (
					<div className="m_l_xl p_l_sm display_flex flex_gap_xs flex_align_center pos_absolute m_t_xs">
						{validationItem(
							selectedMarkers.size >= 3
								? validationItemState.valid
								: validationItemState.warning,
							{
								[validationItemState.valid]: 'Suficientes Motos seleccionadas',
								[validationItemState.warning]:
									'Recomendamos seleccionar mínimo 3 motos',
							},
							true,
						)}
					</div>
				);
			case CreateRouteSteps.filterRepossessions:
				return (
					<div className="m_l_xl p_l_sm display_flex flex_gap_xs flex_align_center pos_absolute m_t_xs">
						{validationItem(
							validationItemState.warning,
							{
								[validationItemState.warning]:
									'Escoje las motos que deseas incluir en la ruta',
							},
							true,
						)}
					</div>
				);
			case CreateRouteSteps.selectInterestPoints:
				return (
					<div className="m_l_xl p_l_sm display_flex flex_gap_xs flex_align_center pos_absolute m_t_xs">
						{validationItem(
							validationItemState.warning,
							{
								[validationItemState.warning]:
									'Haz click en cada punto de interés para tener información adicional',
							},
							true,
						)}
					</div>
				);
			case CreateRouteSteps.createRoute:
				return (
					<div className="m_l_xl p_l_sm display_flex flex_gap_xs flex_align_center pos_absolute m_t_xs">
						{validationItem(
							validationItemState.warning,
							{
								[validationItemState.warning]:
									'Organiza los puntos de interes por cada moto',
							},
							true,
						)}
					</div>
				);
			default:
				return null;
		}
	}, [createRouteStep, selectedMarkers]);

	return (
		<div className="dim_100_per flex_content_col inner_container bg_neutral_300">
			<div className="w_100_per bg_neutral_0 flex_header p_x_lg p_y_md">
				<div className="display_flex flex_align_center flex_justify_between m_b_md">
					<Typography
						scale="heading2"
						weight="600"
						className="text_primary_300"
					>
						Recuperaciones
					</Typography>
					<div className="display_flex flex_align_center flex_gap_sm p_x_xs p_y_xxs bg_neutral_300 br_xs">
						{Object.entries(RepossessionTabsValues).map(([tab, title]) => (
							<Typography
								scale="medium"
								key={tab}
								weight={tab === viewTab ? '600' : '400'}
								textColor={tab === viewTab ? 'neutral_1000' : 'neutral_700'}
								className={`p_x_md p_y_xxs br_xs cursor_pointer ${
									tab === viewTab ? 'bg_neutral_0 shadow_soft' : ''
								}`}
								onClick={() => setViewTab(tab as RepossessionTabs)}
							>
								{title}
							</Typography>
						))}
					</div>
				</div>
				{!createRouteStep && (
					<>
						<Typography
							scale="heading4"
							weight="400"
							textColor="neutral_1000"
							className="m_b_md"
						>
							¡Hola! Has click en el botón para crear una ruta.
						</Typography>
						<Button
							onClick={() =>
								setCreateRouteStep(CreateRouteSteps.allRepossessions)
							}
						>
							Empezar nueva ruta
						</Button>
					</>
				)}
				{createRouteStep && (
					<div className="display_flex flex_align_center flex_justify_strech w_100_per m_b_xl">
						{Array.from(CreateRouteStepsValues).map(([step, value]) => (
							<div className="flex_grow_1" key={step}>
								<div className="display_flex flex_align_center flex_gap_sm ">
									{(step as number) > 1 && (
										<div
											className={`h_xxxs flex_grow_1 ${
												step <= createRouteStep
													? 'bg_primary_300'
													: 'bg_neutral_500'
											}`}
										/>
									)}
									<div
										className={`dim_xl br_circle pos_relative border_1 border_solid flex_shrink_0 ${
											step <= createRouteStep
												? 'border_primary_300'
												: 'border_neutral_500'
										} ${step < createRouteStep && 'bg_primary_300'}`}
									>
										{step < createRouteStep ? (
											<Check className="dim_lg pos_absolute center text_neutral_0 border_transparent" />
										) : (
											<Typography
												scale="small"
												weight="400"
												textColor={
													step === createRouteStep
														? 'primary_300'
														: 'neutral_1000'
												}
												className="pos_absolute center"
											>
												{step}
											</Typography>
										)}
									</div>
									<Typography
										scale={step === createRouteStep ? 'medium' : 'small'}
										weight={step === createRouteStep ? '600' : '400'}
										textColor="neutral_1000"
									>
										{value}
									</Typography>
									{(step as number) < CreateRouteStepsValues.size && (
										<div
											className={`h_xxxs flex_grow_1 ${
												step <= createRouteStep
													? 'bg_primary_300'
													: 'bg_neutral_500'
											}`}
										/>
									)}
								</div>
								{step === createRouteStep && stepValidations}
							</div>
						))}
					</div>
				)}
			</div>
			<div className="flex_grow_1 overflow_hidden pos_relative">
				{(!createRouteStep ||
					createRouteStep === CreateRouteSteps.allRepossessions) && (
					<AllRepossessionsMap />
				)}
				{createRouteStep === CreateRouteSteps.filterRepossessions && (
					<FilterRepossessionsTable />
				)}
				{createRouteStep === CreateRouteSteps.selectInterestPoints && (
					<SelectInterestPointsMap />
				)}
				{createRouteStep === CreateRouteSteps.createRoute && <CreateRouteMap />}
			</div>
		</div>
	);
};
